/** Path of the magic demo link. The password rides in its URL fragment. */
export const DEMO_ROUTE_PATH = "/demo";

/**
 * Fixed identity every demo link authenticates as. Sphinx's demo bypass accepts
 * any email with the shared demo password, but pinning one keeps Sentry, sphinx
 * logs and the avatar menu readable.
 */
export const DEMO_EMAIL = "demo@queen.one";

let capturedFragment: string | null = null;

function isDemoPath(pathname: string): boolean {
  return (pathname.replace(/\/+$/, "") || "/") === DEMO_ROUTE_PATH;
}

/**
 * Reads the demo password out of the URL fragment and takes it off the address
 * bar. Synchronous and idempotent: call it as main.tsx's first statement (the
 * fragment must be gone before `Sentry.init`, which turns on
 * httpContextIntegration and `sendDefaultPii`) and again from the route, which
 * gets back what the first call captured.
 *
 * Keeping the password in the fragment is what keeps it out of Firebase,
 * Cloudflare and Cloud Function access logs and out of `Referer` — fragments
 * are never sent to a server. Be precise about what that is worth: `hexy888`
 * lives in plain, non-secret Helm values and any prospect can read it out of
 * their own login POST. This is about not parking a credential-shaped string in
 * our logs and in Sentry, not about confidentiality.
 *
 * Scoped to `/demo` on purpose — SKU Store uses fragments as real deep links
 * (`/sku-store/$siteId/$skuId#pricing`), which must survive a cold load.
 */
export function captureDemoFragment(): string {
  if (typeof window === "undefined") return "";
  if (!isDemoPath(window.location.pathname)) return "";

  if (capturedFragment === null) {
    capturedFragment = window.location.hash.replace(/^#/, "");
  }
  if (window.location.hash) {
    // Carry the existing state through rather than blanking it. TanStack's
    // browser history keeps its position counter there (`__TSR_index`, plus
    // the entry key), and canGoBack() is `__TSR_index !== 0`, so replacing it
    // with null silently tells the app this entry is the start of the session.
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  }
  return capturedFragment;
}

/**
 * What the route should call: returns the captured password and forgets it.
 *
 * The cache exists only to carry the fragment across main.tsx stripping it
 * before the route mounts, and it lives at module scope, so it outlives any
 * client-side navigation. Left in place it would make a later bare `/demo`
 * visit (a Back button, a bookmark) look exactly like a fresh demo link and
 * silently replace whatever session the visitor had by then.
 */
export function consumeDemoFragment(): string {
  const fragment = captureDemoFragment();
  capturedFragment = null;
  return fragment;
}

/** Drops any `#fragment` from a URL recorded on a Sentry event. */
export function stripUrlFragment(url: string | undefined): string | undefined {
  if (!url) return url;
  const hashIndex = url.indexOf("#");
  return hashIndex === -1 ? url : url.slice(0, hashIndex);
}

/** Test-only: forget the captured fragment so the next call re-reads the URL. */
export function resetCapturedDemoFragment(): void {
  capturedFragment = null;
}
